import React, { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreditCard, CheckCircle2, AlertTriangle, Clock, ExternalLink, Ban, FileText, Download } from "lucide-react";
import { translatePlanText, translatePlanMaxTech } from "@/lib/planTranslate";

const STATUS_KEYS = {
  trial: { key: "plans.status_trial", color: "bg-sky-100 text-sky-800 border-sky-200" },
  active: { key: "plans.status_active", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  past_due: { key: "plans.status_past_due", color: "bg-amber-100 text-amber-800 border-amber-200" },
  suspended: { key: "plans.status_suspended", color: "bg-red-100 text-red-800 border-red-200" },
  canceled: { key: "plans.status_canceled", color: "bg-slate-200 text-slate-700 border-slate-300" },
};

export default function AbbonamentoPage() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);

  const load = async () => {
    try {
      const [s, p] = await Promise.all([api.get("/subscription/status"), api.get("/subscription/plans")]);
      setStatus(s.data);
      setPlans(p.data);
      if (s.data.has_stripe_customer) {
        try {
          const inv = await api.get("/subscription/invoices");
          setInvoices(inv.data);
        } catch (_) { /* ignore */ }
      }
      // Solo admin: fetch stato Stripe per diagnostica
      try {
        const ss = await api.get("/admin/stripe-status");
        setStripeStatus(ss.data);
      } catch (_) { /* non-admin ignora */ }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const goCheckout = async (planId) => {
    setLoading(true);
    try {
      const { data } = await api.post("/subscription/checkout", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setLoading(false); }
  };

  const openPortal = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/subscription/portal", { return_url: window.location.href });
      window.location.href = data.portal_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setLoading(false); }
  };

  if (!status) return <div className="p-8 text-center text-slate-400">{t("auto.caricamento")}</div>;

  const st = STATUS_KEYS[status.status] || STATUS_KEYS.trial;
  const endDate = status.current_period_end || status.trial_ends_at;

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.abbonamento")}</div>
        <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">{t("menu.subscription")}</h1>
      </div>

      {status.warning && (
        <div className="rounded-sm border border-amber-300 bg-amber-50 p-4 flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-700" />
          <div className="flex-1 text-sm text-amber-900">{status.warning}</div>
        </div>
      )}

      {stripeStatus && (
        <div
          className={`rounded-sm border p-3 text-xs ${
            stripeStatus.detected_mode === "live" && stripeStatus.charges_enabled
              ? "border-emerald-200 bg-emerald-50 text-emerald-900"
              : "border-red-300 bg-red-50 text-red-900"
          }`}
          data-testid="stripe-status-badge"
        >
          <div className="flex flex-wrap items-center gap-2">
            <b>{t("plans.stripe_status_label")}</b>
            <span className="rounded-sm border px-2 py-0.5 font-mono uppercase">
              {stripeStatus.detected_mode === "live" ? "🟢 LIVE" : "🔴 TEST"}
            </span>
            <span>chiave <code className="font-mono">{stripeStatus.key_prefix}...</code></span>
            {stripeStatus.account_id && <span>· account <code className="font-mono">{stripeStatus.account_id}</code> ({stripeStatus.country})</span>}
            {"charges_enabled" in stripeStatus && (
              <span>· pagamenti: {stripeStatus.charges_enabled ? "✅" : "❌"}</span>
            )}
          </div>
          {stripeStatus.detected_mode !== "live" && (
            <div className="mt-1">
              ⚠️ La produzione risulta in <b>{t("auto.modalità_test")}</b>. Le chiavi live non sono state applicate. Devi rideployare l'app oppure verificare le variabili d'ambiente in Emergent Dashboard.
            </div>
          )}
          {stripeStatus.stripe_error && (
            <div className="mt-1">❌ Errore Stripe: {stripeStatus.stripe_error}</div>
          )}
        </div>
      )}

      <div className="rounded-sm border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("plans.status_kicker")}</div>
            <div className="mt-1 flex items-center gap-2">
              <span className={`inline-block rounded-sm border px-3 py-1 text-sm font-bold uppercase tracking-wider ${st.color}`}>{t(st.key)}</span>
              {status.plan_name && <span className="text-sm text-slate-700">— <b>{status.plan_name}</b> · €{status.plan_price_eur}{t("plans.per_month")}</span>}
            </div>
            {endDate && (
              <div className="mt-2 flex items-center gap-1 text-xs text-slate-600">
                <Clock className="h-3 w-3" />
                {status.status === "trial" ? t("plans.trial_end_label") : t("plans.next_renewal_label")}
                <b className="ml-1">{new Date(endDate).toLocaleString()}</b>
                {status.days_left != null && <span className="ml-1">· {t("plans.days_remaining", { days: Math.floor(status.days_left) })}</span>}
              </div>
            )}
          </div>
          {status.has_stripe_customer && (
            <div className="flex flex-col items-end gap-1">
              <Button onClick={openPortal} disabled={loading} variant="outline" className="rounded-sm" data-testid="btn-open-portal">
                <ExternalLink className="mr-2 h-4 w-4" /> {t("plans.gestisci_disdici")}
              </Button>
              <div className="text-[10px] text-slate-500">{t("plans.portal_hint")}</div>
            </div>
          )}
        </div>
        {status.status === "trial" && status.has_stripe_customer && (
          <div className="mt-3 rounded-sm border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
            💡 <b>{t("plans.you_are_in_trial")}</b> <Trans i18nKey="plans.trial_warning_hint" components={[<span key="0" />, <b key="1" />]} />
          </div>
        )}
      </div>

      <div>
        <h2 className="mb-3 text-lg font-bold text-slate-900">{t("plans.choose_or_change")}</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {plans.map((p) => {
            const isCurrent = status.plan_id === p.id;
            return (
              <div key={p.id} className={`rounded-sm border p-5 ${isCurrent ? "border-[#0052FF] bg-blue-50" : "border-slate-200 bg-white"}`} data-testid={`sub-plan-${p.id}`}>
                <div className="text-lg font-bold text-slate-900">{p.name}</div>
                {p.description && <div className="mt-0.5 text-xs text-slate-500">{translatePlanText(p.description, t)}</div>}
                <div className="mt-3 text-4xl font-bold text-[#0052FF]">€ {Number(p.price_eur).toFixed(0)}<span className="text-sm font-normal text-slate-500">{t("plans.per_month")}</span></div>
                {p.max_technicians && <div className="mt-1 text-xs text-slate-600">{translatePlanMaxTech(p.max_technicians, t)}</div>}
                {(p.features || []).length > 0 && (
                  <ul className="mt-4 space-y-1 text-sm text-slate-700">
                    {p.features.map((f, i) => <li key={i} className="flex gap-2"><CheckCircle2 className="h-4 w-4 flex-shrink-0 text-emerald-600" /> {translatePlanText(f, t)}</li>)}
                  </ul>
                )}
                <Button
                  onClick={() => goCheckout(p.id)}
                  disabled={loading || isCurrent}
                  className="mt-5 w-full rounded-sm bg-[#0052FF] hover:bg-[#0040CC]"
                  data-testid={`sub-plan-select-${p.id}`}
                >
                  {isCurrent ? t("plans.current_plan") : t("plans.activate_btn")}
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {invoices.length > 0 && (
        <div>
          <h2 className="mb-3 mt-8 flex items-center gap-2 text-lg font-bold text-slate-900">
            <FileText className="h-5 w-5" /> {t("plans.your_invoices")}
          </h2>
          <div className="overflow-x-auto rounded-sm border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-bold uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="p-3">{t("auto.numero")}</th>
                  <th className="p-3">{t("common.date")}</th>
                  <th className="p-3">{t("plans.period_col")}</th>
                  <th className="p-3 text-right">{t("plans.amount_col")}</th>
                  <th className="p-3">{t("common.status")}</th>
                  <th className="p-3 text-right">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`invoice-row-${inv.id}`}>
                    <td className="p-3 font-mono text-xs">{inv.invoice_number || "—"}</td>
                    <td className="p-3 text-xs">{inv.created_at ? new Date(inv.created_at).toLocaleDateString("it-IT") : "—"}</td>
                    <td className="p-3 text-xs">
                      {inv.period_start && inv.period_end
                        ? `${new Date(inv.period_start).toLocaleDateString("it-IT")} → ${new Date(inv.period_end).toLocaleDateString("it-IT")}`
                        : "—"}
                    </td>
                    <td className="p-3 text-right font-mono font-semibold">€ {Number(inv.amount_paid || 0).toFixed(2)}</td>
                    <td className="p-3"><Badge className="bg-emerald-100 text-emerald-800">{inv.status}</Badge></td>
                    <td className="p-3 text-right">
                      {inv.invoice_pdf && (
                        <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer" data-testid={`invoice-pdf-${inv.id}`}>
                          <Button size="sm" variant="outline" className="rounded-sm">
                            <Download className="mr-1 h-3 w-3" /> PDF
                          </Button>
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
